import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from './contexts/AdminAuthContext';
import AdminLoginPage from './pages/LoginPage';
import AdminDashboardPage from './pages/DashboardPage';
import AdminDriversPage from './pages/DriversPage';
import AdminDriverGpsHealthPage from './pages/DriverGpsHealthPage';
import AdminDriverDetailPage from './pages/DriverDetailPage';
import AdminPrintReportPage from './pages/PrintReportPage';
import AdminMapPage from './pages/MapPage';
import AdminMaintenancePageComponent from './pages/MaintenancePage';
import AdminFleetPageComponent from './pages/FleetPage';
import SaaqInspectionPage from './pages/SaaqInspectionPage';
import BrakeTireMeasurementPage from './pages/BrakeTireMeasurementPage';
import AdminSettingsPage from './pages/SettingsPage';
import ClientPortalPage from './pages/ClientPortalPage';
import OrdersGridPage from './pages/OrdersGridPage';
import DriverPlanningPage from './pages/DriverPlanningPage';
import RepartitionMockupPreviewPage from './pages/mockup/RepartitionMockupPreviewPage';
import RegisterPage from './pages/RegisterPage';
import { PaymentSuccessPage, PaymentCancelPage } from './pages/PaymentPage';
import MecanicienHubPage from './pages/MecanicienHubPage';

const LegacyWorkOrderRedirect = () => {
  const { orderId } = useParams();
  return <Navigate to={`/admin/mecanicien/bons/${orderId}`} replace />;
};
import AdminAtelierPage from './pages/AdminAtelierPage';
import SacAPlanifierPage from './pages/SacAPlanifierPage';
import SacPlanifiesPage from './pages/SacPlanifiesPage';
import SacAttribueesPage from './pages/SacAttribueesPage';
import RouteOptimisationPage from './pages/RouteOptimisationPage';
import AdminAgentVentesPage from './pages/AdminAgentVentesPage';
import AtelierFleetPage from './pages/AtelierFleetPage';
import BillingPage from './pages/BillingPage';
import AdminConnectionsPage from './pages/AdminConnectionsPage';
import ExpensesPage from './pages/ExpensesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import RateAgentPage from './pages/RateAgentPage';
import AdminMessagesPage from './pages/AdminMessagesPage'; // MSG-SYSTEM-V1
import AdminLayout from './components/Layout';
import { Lock, ArrowUpRight } from 'lucide-react';

const PLAN_NAMES = {
  localisation: 'Localisation (39.99$/mois)',
  repartition: 'Repartition (69.99$/mois)',
  facturation: 'Facturation (99.99$/mois)',
  pro: 'ProPilote Pro (149.99$/mois)',
};

const UpgradeRequired = ({ requiredPlan, featureName }) => {
  const navigate = useNavigate();
  const planLabel = PLAN_NAMES[requiredPlan] || requiredPlan;

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4" data-testid="upgrade-required-screen">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center max-w-md w-full">
        <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock size={28} className="text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Mise a niveau requise</h2>
        <p className="text-slate-400 mb-1">
          La fonctionnalite <span className="text-white font-medium">{featureName}</span> necessite le plan :
        </p>
        <p className="text-amber-500 font-semibold mb-6">{planLabel}</p>
        <button
          onClick={() => navigate('/admin/settings')}
          className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-6 py-3 rounded-lg transition-colors flex items-center gap-2 mx-auto"
          data-testid="go-to-upgrade-btn"
        >
          Voir les plans <ArrowUpRight size={16} />
        </button>
      </div>
    </div>
  );
};

const AdminProtectedRoute = ({ children, requiredModule, requiredFeatures, requiredPlan, featureName, adminOnly, pageKey }) => {
  const { isAuthenticated, loading, hasModule, hasPage, hasFeature, isAtelier, isMechanic, subscriptionStatus, user } = useAdminAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (adminOnly && isAtelier) {
    return <Navigate to="/admin/fleet" replace />;
  }

  if (adminOnly && isMechanic) {
    return <Navigate to="/admin/mecanicien" replace />;
  }

  // INTERNAL-USERS-V6 : verification permission par page
  if (pageKey && !hasPage(pageKey)) {
    if (user?.role === 'agent_ventes') return <Navigate to="/admin/agent-ventes" replace />;
    if (user?.role === 'service_client') return <Navigate to="/admin/service-client" replace />;
    return <Navigate to="/admin/settings" replace />;
  }

  // V12 : si l'employe a la permission page-level, l'admin a explicitement autorise l'acces => bypass requiredModule
  const isInternalUser = ['service_client', 'agent_ventes', 'admin_entretiens', 'admin_repartition', 'admin_facturation', 'mechanic', 'atelier'].includes(user?.role);
  const hasPagePermission = pageKey && isInternalUser && hasPage(pageKey);
  if (!hasPagePermission && requiredModule && !hasModule(requiredModule)) {
    return <UpgradeRequired requiredPlan={requiredPlan || 'pro'} featureName={featureName || 'ce module'} />;
  }

  if (requiredFeatures && requiredFeatures.length > 0) {
    const missingFeature = requiredFeatures.find(f => !hasFeature(f));
    if (missingFeature) {
      return <UpgradeRequired requiredPlan={requiredPlan || 'pro'} featureName={featureName || 'cette fonctionnalite'} />;
    }
  }

  if (subscriptionStatus.isExpired && requiredModule) {
    return <UpgradeRequired requiredPlan="entretien" featureName={featureName || 'ce module'} />;
  }

  return children;
};

const RoleBasedRedirect = () => {
  const { isAtelier, isMechanic, isAgentVentes, isServiceClient } = useAdminAuth();
  if (isMechanic) return <Navigate to="mecanicien" replace />;
  if (isAtelier) return <Navigate to="fleet" replace />;
  if (isAgentVentes) return <Navigate to="agent-ventes" replace />;
  if (isServiceClient) return <Navigate to="service-client" replace />;
  return <Navigate to="dashboard" replace />;
};

const AdminFleetPage = () => {
  const { token, isAtelier } = useAdminAuth();
  return <AdminFleetPageComponent token={token} mode={isAtelier ? 'atelier' : 'admin'} />;
};

const AdminMaintenancePage = () => {
  const { token, isAtelier } = useAdminAuth();
  return <AdminMaintenancePageComponent token={token} mode={isAtelier ? 'atelier' : 'admin'} />;
};

const AdminRoutes = () => {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="login" element={<AdminLoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="payment/success" element={<PaymentSuccessPage />} />
        <Route path="payment/cancel" element={<PaymentCancelPage />} />
        {/* Mockup preview (public — pas de login requis, pour validation design) */}
        <Route path="repartition-mockup" element={<RepartitionMockupPreviewPage />} />
        
        <Route path="print-report" element={
          <AdminProtectedRoute requiredModule="module_entretiens" featureName="Rapports">
            <AdminPrintReportPage />
          </AdminProtectedRoute>
        } />
        
        <Route path="/" element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        }>
          <Route index element={<RoleBasedRedirect />} />
          <Route path="dashboard" element={
            <AdminProtectedRoute adminOnly featureName="Tableau de bord" pageKey="page_dashboard" pageKey="page_dashboard">
              <AdminDashboardPage />
            </AdminProtectedRoute>
          } />
          <Route path="drivers" element={
            <AdminProtectedRoute 
              requiredModule="module_entretiens" 
              requiredFeatures={['carte_en_direct']} 
              requiredPlan="localisation"
              featureName="Chauffeurs"
              adminOnly
              pageKey="page_drivers"
              pageKey="page_drivers"
            >
              <AdminDriversPage />
            </AdminProtectedRoute>
          } />
          <Route path="drivers/:driverId" element={
            <AdminProtectedRoute 
              requiredModule="module_entretiens" 
              requiredFeatures={['carte_en_direct']} 
              requiredPlan="localisation"
              featureName="Detail chauffeur"
              adminOnly
              pageKey="page_drivers"
              pageKey="page_drivers"
            >
              <AdminDriverDetailPage />
            </AdminProtectedRoute>
          } />
          <Route path="gps-health" element={
            <AdminProtectedRoute
              adminOnly
              featureName="Sante GPS"
              pageKey="page_drivers"
            >
              <AdminDriverGpsHealthPage />
            </AdminProtectedRoute>
          } />
          <Route path="map" element={
            <AdminProtectedRoute 
              requiredModule="module_entretiens" 
              requiredFeatures={['carte_en_direct']} 
              requiredPlan="localisation"
              featureName="Carte en direct"
              adminOnly
              pageKey="page_map"
              pageKey="page_map"
            >
              <AdminMapPage />
            </AdminProtectedRoute>
          } />
          <Route path="maintenance" element={
            <AdminProtectedRoute requiredModule="module_entretiens" featureName="Entretiens" pageKey="page_entretiens">
              <AdminMaintenancePage />
            </AdminProtectedRoute>
          } />
          <Route path="fleet" element={
            <AdminProtectedRoute requiredModule="module_entretiens" featureName="Flotte" pageKey="page_flotte">
              <AdminFleetPage />
            </AdminProtectedRoute>
          } />
          <Route path="fleet/:vehicleId/inspection" element={
            <AdminProtectedRoute requiredModule="module_entretiens" featureName="Inspection Mécanique" pageKey="page_flotte">
              <SaaqInspectionPage />
            </AdminProtectedRoute>
          } />
          <Route path="fleet/:vehicleId/measurements" element={
            <AdminProtectedRoute requiredModule="module_entretiens" featureName="Mesures freins/pneus" pageKey="page_flotte">
              <BrakeTireMeasurementPage />
            </AdminProtectedRoute>
          } />
          {/* LOGISTIQUE-PLUS-V2 - Ancienne page Kanban conservee comme alias */}
          <Route path="service-client" element={<Navigate to="/admin/service-client/a-planifier" replace />} />
          <Route path="service-client/a-planifier" element={
            <AdminProtectedRoute
              requiredModule="module_logistique_plus"
              requiredPlan="logistique_plus"
              featureName="RDV à planifier"
              adminOnly
              pageKey="page_service_client"
              pageKey="page_service_client"
            >
              <SacAPlanifierPage />
            </AdminProtectedRoute>
          } />
          <Route path="service-client/planifies" element={
            <AdminProtectedRoute
              requiredModule="module_logistique_plus"
              requiredPlan="logistique_plus"
              featureName="RDV planifiés"
              adminOnly
              pageKey="page_service_client"
            >
              <SacPlanifiesPage />
            </AdminProtectedRoute>
          } />
          <Route path="service-client/attribuees" element={
            <AdminProtectedRoute
              requiredModule="module_logistique_plus"
              requiredPlan="logistique_plus"
              featureName="Attribuées au répartiteur"
              adminOnly
              pageKey="page_service_client"
            >
              <SacAttribueesPage />
            </AdminProtectedRoute>
          } />
          <Route path="route-optimisation" element={
            <AdminProtectedRoute
              requiredPermissions={['view_dispatch_board']}
              featureName="Route Optimisation"
              pageKey="page_dispatch"
            >
              <RouteOptimisationPage />
            </AdminProtectedRoute>
          } />
          {/* Ancienne page Kanban supprimee : /admin/service-client redirige vers /a-planifier */}
          {/* LOGISTIQUE-PLUS-V3 - Formulaire Rep (agent_ventes) */}
          <Route path="agent-ventes" element={
            <AdminProtectedRoute
              requiredModule="module_logistique_plus"
              requiredPlan="logistique_plus"
              featureName="Nouvelle commande"
              pageKey="page_agent_ventes"
            >
              <AdminAgentVentesPage />
            </AdminProtectedRoute>
          } />
          <Route path="dispatch" element={<Navigate to="/admin/commandes" replace />} />
          <Route path="commandes" element={
            <AdminProtectedRoute 
              requiredPermissions={['view_dispatch_board']}
              pageKey="page_dispatch"
            >
              <OrdersGridPage />
            </AdminProtectedRoute>
          } />
          <Route path="planning-chauffeurs" element={
            <AdminProtectedRoute 
              requiredPermissions={['view_dispatch_board']}
              pageKey="page_dispatch"
            >
              <DriverPlanningPage />
            </AdminProtectedRoute>
          } />
          <Route path="client-portal" element={
            <AdminProtectedRoute 
              requiredModule="module_repartition"
              requiredFeatures={['portail_clients']} 
              requiredPlan="repartition"
              featureName="Portail Clients"
              adminOnly
              pageKey="page_portail_clients"
              pageKey="page_portail_clients"
            >
              <ClientPortalPage />
            </AdminProtectedRoute>
          } />
          {/* Mechanic routes */}
          <Route path="atelier" element={
            <AdminProtectedRoute requiredModule="module_entretiens" featureName="Atelier" pageKey="page_atelier">
              <AdminAtelierPage />
            </AdminProtectedRoute>
          } />
          <Route path="fleet-atelier" element={
            <AdminProtectedRoute requiredModule="module_entretiens" featureName="Flotte Atelier" pageKey="page_flotte">
              <AtelierFleetPage />
            </AdminProtectedRoute>
          } />
          <Route path="mecanicien/*" element={
            <AdminProtectedRoute requiredModule="module_entretiens" featureName="Mécanicien" pageKey="page_atelier">
              <MecanicienHubPage />
            </AdminProtectedRoute>
          } />
          <Route path="mechanic" element={<Navigate to="/admin/mecanicien" replace />} />
          <Route path="mechanic/work-orders" element={<Navigate to="/admin/mecanicien/bons" replace />} />
          <Route path="mechanic/work-orders/:orderId" element={<LegacyWorkOrderRedirect />} />
          <Route path="connections" element={
            <AdminProtectedRoute requiredModule="module_entretiens" featureName="Connexions" pageKey="page_settings">
              <AdminConnectionsPage />
            </AdminProtectedRoute>
          } />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="billing" element={
            <AdminProtectedRoute 
              requiredModule="module_facturation" 
              requiredPlan="facturation"
              featureName="Facturation"
              adminOnly
              pageKey="page_facturation"
              pageKey="page_facturation"
            >
              <BillingPage />
            </AdminProtectedRoute>
          } />
          <Route path="expenses" element={
            <AdminProtectedRoute adminOnly featureName="Comptabilite" pageKey="page_comptabilite">
              <ExpensesPage />
            </AdminProtectedRoute>
          } />
          <Route path="analytics" element={
            <AdminProtectedRoute adminOnly featureName="Analytics" pageKey="page_analytics">
              <AnalyticsPage />
            </AdminProtectedRoute>
          } />
          <Route path="rate-agent" element={
            <AdminProtectedRoute adminOnly featureName="Rate-Agent IA">
              <RateAgentPage />
            </AdminProtectedRoute>
          } />
        </Route>
        
        <Route path="*" element={<RoleBasedRedirect />} />
      </Routes>
    </AdminAuthProvider>
  );
};

export default AdminRoutes;
